import { CircularProgress, Switch } from '@mui/material';
import cn from 'classnames';
import { FC, useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AssessmentMe,
  AssessmentSummary,
  AssessmentWindow,
  AssessmentWindowSummary,
  assessmentsApi,
} from 'src/api/assessments';

import { useTabActive } from '../Tabs/tabActiveContext';
import styles from './Assessment.module.scss';
import { AssessmentForm, AssessmentFormValues } from './AssessmentForm';
import {
  ANSWER_NOW_LABEL,
  BAND_LABEL,
  CHANGE_TEXT,
  CHANGE_TITLE,
  CONSENT_FAILED_TEXT,
  CONSENT_SAVED_TEXT,
  LEADER_INTRO,
  LEADER_TITLE,
  LEGEND_TEXT,
  LOADING_TEXT,
  MEMBER_INTRO,
  MEMBER_TITLE,
  OWN_ANSWERS_TITLE,
  RESEARCH_LABEL,
  RESEARCH_TEXT,
  SHOWN_WINDOWS,
  SUBMITTED_TEXT,
  SUBMIT_FAILED_TEXT,
  UNAVAILABLE_TEXT,
  WINDOW_LABEL,
  answerLabel,
  answeredText,
  bandCounts,
  bandsLabel,
  bothTimesText,
  formatChange,
  formatMean,
  nextWindow,
  notOpenText,
  openText,
  responseFor,
  shortItemText,
  withheldText,
} from './assessmentText';

interface AssessmentTabProps {
  circleId: string;
  isLeader: boolean;
  /** Changes when the member answered elsewhere (the dialog), to refetch. */
  refreshKey?: number;
}

type MemberState =
  { kind: 'loading' } | { kind: 'ready'; me: AssessmentMe } | { kind: 'error' };

type LeaderState =
  | { kind: 'loading' }
  | { kind: 'ready'; summary: AssessmentSummary }
  | { kind: 'error' };

/**
 * The "Måling" tab of a LONELINESS Circle: a member's own numbers and
 * answers with the research switch, or the leader's group numbers, withheld
 * under the minimum. Fetched each time the tab is opened.
 */
export const AssessmentTab: FC<AssessmentTabProps> = ({
  circleId,
  isLeader,
  refreshKey = 0,
}) =>
  isLeader ? (
    <LeaderView circleId={circleId} />
  ) : (
    <MemberView circleId={circleId} refreshKey={refreshKey} />
  );

const Loading: FC = () => (
  <div className={styles.loading} role="status">
    <CircularProgress size={28} />
    <span>{LOADING_TEXT}</span>
  </div>
);

const MemberView: FC<{ circleId: string; refreshKey: number }> = ({
  circleId,
  refreshKey,
}) => {
  const isActive = useTabActive();
  const [state, setState] = useState<MemberState>({ kind: 'loading' });
  const [answering, setAnswering] = useState<AssessmentWindow | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [savingConsent, setSavingConsent] = useState(false);

  const load = useCallback(() => {
    let cancelled = false;
    assessmentsApi
      .getMe(circleId)
      .then((me) => {
        if (!cancelled) {
          setState({ kind: 'ready', me });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ kind: 'error' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [circleId]);

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }
    return load();
  }, [isActive, refreshKey, load]);

  if (state.kind === 'loading') {
    return <Loading />;
  }
  if (state.kind === 'error') {
    return <p className={styles.text}>{UNAVAILABLE_TEXT}</p>;
  }

  const { me } = state;
  const next = me.can_answer ? nextWindow(me.responses) : null;
  const latest = [...me.responses]
    .filter((response) => SHOWN_WINDOWS.includes(response.window))
    .sort(
      (a, b) =>
        SHOWN_WINDOWS.indexOf(b.window) - SHOWN_WINDOWS.indexOf(a.window)
    )[0];
  const consentResearch = me.responses.some(
    (response) => response.consent_research
  );

  const handleSubmit = async (
    window: AssessmentWindow,
    values: AssessmentFormValues
  ) => {
    setSubmitting(true);
    try {
      const response = await assessmentsApi.submit(circleId, {
        window,
        answers: values.answers,
        consent_service: true,
        consent_research: values.consentResearch,
      });
      toast.success(SUBMITTED_TEXT);
      setAnswering(null);
      setState({
        kind: 'ready',
        me: { ...me, responses: [...me.responses, response] },
      });
    } catch {
      toast.error(SUBMIT_FAILED_TEXT);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConsent = async (value: boolean) => {
    setSavingConsent(true);
    try {
      await assessmentsApi.setResearchConsent(circleId, value);
      toast.success(CONSENT_SAVED_TEXT);
      setState({
        kind: 'ready',
        me: {
          ...me,
          responses: me.responses.map((response) => ({
            ...response,
            consent_research: value,
          })),
        },
      });
    } catch {
      toast.error(CONSENT_FAILED_TEXT);
    } finally {
      setSavingConsent(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>{MEMBER_TITLE}</h2>
        <p className={styles.text}>{MEMBER_INTRO}</p>
      </div>

      <div className={styles.cards}>
        {SHOWN_WINDOWS.map((window) => {
          const response = responseFor(me.responses, window);
          const isNext = next === window;
          return (
            <div
              key={window}
              className={cn(styles.card, { [styles.cardPending]: !response })}
            >
              <div className={styles.cardLabel}>{WINDOW_LABEL[window]}</div>
              {response ? (
                <>
                  <div className={styles.score}>
                    {response.score}
                    <span className={styles.scoreUnit}>
                      {' '}
                      av {me.instrument.score_max}
                    </span>
                  </div>
                  <div className={styles.quiet}>{WINDOW_LABEL[window]}</div>
                </>
              ) : (
                <>
                  <div className={styles.scoreEmpty} aria-hidden="true">
                    ·
                  </div>
                  {isNext ? (
                    <>
                      <div className={styles.quiet}>{openText}</div>
                      {answering === window ? null : (
                        <button
                          type="button"
                          className={styles.secondaryButton}
                          onClick={() => setAnswering(window)}
                        >
                          {ANSWER_NOW_LABEL}
                        </button>
                      )}
                    </>
                  ) : (
                    <div className={styles.quiet}>{notOpenText}</div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {answering ? (
        <div className={styles.formInline}>
          <AssessmentForm
            instrument={me.instrument}
            window={answering}
            initialConsentResearch={consentResearch}
            submitting={submitting}
            onSubmit={(values) => handleSubmit(answering, values)}
            onLater={() => setAnswering(null)}
          />
        </div>
      ) : null}

      {latest ? (
        <div className={styles.answers}>
          <h3 className={styles.answersTitle}>
            {OWN_ANSWERS_TITLE(latest.window)}
          </h3>
          {me.instrument.items.map((item) => {
            const answer = latest.answers.find(
              (entry) => entry.item_id === item.id
            );
            return (
              <div key={item.id} className={styles.answerRow}>
                <span>{shortItemText(item.text)}</span>
                <span className={styles.answerValue}>
                  {answer ? answerLabel(me.instrument, answer.value) : ''}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}

      {me.responses.length > 0 ? (
        <div className={styles.research}>
          <div>
            <label
              htmlFor="assessment-research"
              className={styles.researchLabel}
            >
              {RESEARCH_LABEL}
            </label>
            <span className={styles.text}>{RESEARCH_TEXT}</span>
          </div>
          <Switch
            id="assessment-research"
            checked={consentResearch}
            disabled={savingConsent}
            onChange={(event) => handleConsent(event.target.checked)}
            color="primary"
          />
        </div>
      ) : null}
    </div>
  );
};

const WindowCard: FC<{
  summary: AssessmentSummary;
  window: AssessmentWindow;
}> = ({ summary, window }) => {
  const entry: AssessmentWindowSummary | undefined = summary.windows.find(
    (item) => item.window === window
  );
  const n = entry?.n ?? 0;
  const shown = entry?.mean !== null && entry?.mean !== undefined;
  const bands =
    shown && entry?.distribution
      ? bandCounts(
          entry.distribution,
          summary.instrument.score_min,
          summary.instrument.score_max
        )
      : null;
  const total = bands ? bands.low + bands.middle + bands.high : 0;

  return (
    <div
      className={cn(styles.card, {
        [styles.cardWithheld]: n > 0 && !shown,
        [styles.cardPending]: n === 0,
      })}
    >
      <div className={styles.cardLabel}>{WINDOW_LABEL[window]}</div>
      {shown && entry ? (
        <div className={styles.score}>
          {formatMean(entry.mean as number)}
          <span className={styles.scoreUnit}>
            {' '}
            av {summary.instrument.score_max}
          </span>
        </div>
      ) : (
        <div className={styles.scoreEmpty} aria-hidden="true">
          ·
        </div>
      )}
      <div className={styles.quiet}>{answeredText(n, summary.members)}</div>
      {bands && total > 0 ? (
        <>
          <div className={styles.bar} role="img" aria-label={bandsLabel(bands)}>
            {bands.low > 0 ? (
              <div className={styles.barLow} style={{ flex: bands.low }} />
            ) : null}
            {bands.middle > 0 ? (
              <div
                className={styles.barMiddle}
                style={{ flex: bands.middle }}
              />
            ) : null}
            {bands.high > 0 ? (
              <div className={styles.barHigh} style={{ flex: bands.high }} />
            ) : null}
          </div>
          <div className={styles.barLegend}>
            <span>
              {BAND_LABEL.low} {bands.low}
            </span>
            <span>
              {BAND_LABEL.middle} {bands.middle}
            </span>
            <span>
              {BAND_LABEL.high} {bands.high}
            </span>
          </div>
        </>
      ) : n > 0 ? (
        <div className={styles.text}>{withheldText(summary.min_n)}</div>
      ) : null}
    </div>
  );
};

const LeaderView: FC<{ circleId: string }> = ({ circleId }) => {
  const isActive = useTabActive();
  const [state, setState] = useState<LeaderState>({ kind: 'loading' });

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }
    let cancelled = false;
    assessmentsApi
      .getSummary(circleId)
      .then((summary) => {
        if (!cancelled) {
          setState({ kind: 'ready', summary });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ kind: 'error' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isActive, circleId]);

  if (state.kind === 'loading') {
    return <Loading />;
  }
  if (state.kind === 'error') {
    return <p className={styles.text}>{UNAVAILABLE_TEXT}</p>;
  }

  const { summary } = state;
  const change = summary.change_before_after;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>{LEADER_TITLE}</h2>
        <p className={styles.text}>{LEADER_INTRO}</p>
      </div>

      <div className={styles.cards}>
        {SHOWN_WINDOWS.map((window) => (
          <WindowCard key={window} summary={summary} window={window} />
        ))}
      </div>

      <div className={styles.change}>
        <div className={styles.header}>
          <div className={styles.answersTitle}>{CHANGE_TITLE}</div>
          <span className={styles.text}>
            {change.mean === null ? CHANGE_TEXT : bothTimesText(change.n)}
          </span>
        </div>
        <div
          className={
            change.mean === null ? styles.scoreEmpty : styles.changeValue
          }
          aria-label={change.mean === null ? CHANGE_TEXT : undefined}
        >
          {change.mean === null ? '·' : formatChange(change.mean)}
        </div>
      </div>

      <div className={styles.legend}>{LEGEND_TEXT}</div>
    </div>
  );
};
